import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import {
  clearAffiliateMeCache,
  getAffiliateMe,
  getAffiliateCommissionHistory,
} from '../../services/affiliateService'
import { getWebSocketConnection } from '../../hooks/useSmartWebSocket'
import { getReferralDisplayPromotionConfig, type UserReferralDisplaySettings } from '../../services/promotionConfigService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMeData, AffiliateCommissionHistoryItem } from '../../services/affiliateService'
import { aggregateCommissionHistoryByDay } from '../../utils/affiliateHistoryUtils'
import {
  DEFAULT_MIN_COMMISSION_WITHDRAW,
  affiliateWithdrawShortfall,
  canAffiliateCommissionWithdraw,
  resolveMinCommissionWithdraw,
} from './affiliateConstants'

function formatHistoryDate(item: AffiliateCommissionHistoryItem): string {
  if (item.date) return item.date
  if (item.createdAt) {
    const d = new Date(item.createdAt)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
  }
  return '–'
}

const DEFAULT_DISPLAY_CONFIG: UserReferralDisplaySettings = {
  pageTitle: 'แนะนำเพื่อน',
  pageSubtitle: 'แชร์ลิงก์ของคุณเพื่อรับค่าคอมมิชชั่นจากยอดเล่นของสมาชิกที่คุณแนะนำ',
  withdrawButtonText: 'ถอนคอมมิชชั่น',
  minCommissionWithdrawalAmount: DEFAULT_MIN_COMMISSION_WITHDRAW,
  shareSectionTitle: 'ลิงก์แนะนำ',
  linkLabel: 'ลิงก์สำหรับโปรโมท',
  copyButtonText: 'คัดลอก',
  shareDescription:
    'ลิงก์ช่วยแชร์รับ {{rate}} ฟรี (แค่ก๊อปปี้ลิงก์ไปแชร์ก็ได้เงินแล้ว) ยิ่งแชร์มากยิ่งได้มาก ท่านสามารถนำลิงก์ด้านล่างนี้ไปแชร์ในช่องทางต่างๆ ไม่ว่าจะเป็น เว็บไซต์ส่วนตัว, Blog, Facebook หรือ Social Network อื่นๆ หากมีการสมัครสมาชิกโดยคลิกผ่านลิงก์ของท่านเข้ามา ลูกค้าที่สมัครเข้ามาก็จะอยู่ภายใต้เครือข่ายของท่านทันที และหากลูกค้าภายใต้เครือข่ายของท่านมีการเดิมพันเข้ามา ทุกยอดการเดิมพันท่านจะได้รับส่วนแบ่งในการแนะนำ {{rate}} ทันทีโดยไม่มีเงื่อนไข',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบแนะนำเพื่อนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่พบรายการ',
  hideZeroCommissionSegments: true,
  promotionRoute: '/affiliate',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'แนะนำเพื่อน',
    badgeText: 'Referral',
    title: 'แนะนำเพื่อน',
    description: 'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
    buttonText: 'ไปหน้าแนะนำเพื่อน',
  },
}

function mergeDisplayConfig(input?: Partial<UserReferralDisplaySettings>): UserReferralDisplaySettings {
  return {
    ...DEFAULT_DISPLAY_CONFIG,
    ...(input || {}),
  }
}

function renderRateTemplate(text: string, rate: string): string {
  return String(text || '').replace(/\{\{\s*rate\s*\}\}/gi, rate || '0.00%')
}

function splitCommissionRate(rate: string): string[] {
  return String(rate || '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function isZeroCommissionSegment(segment: string): boolean {
  const match = String(segment || '').match(/(-?\d+(?:\.\d+)?)\s*%/)
  return match ? Number(match[1]) === 0 : false
}

const AffiliateOverviewPage: React.FC = () => {
  const { themeConfig } = useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [affiliateDisabled, setAffiliateDisabled] = useState(false)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])
  const [displayConfig, setDisplayConfig] = useState<UserReferralDisplaySettings>(DEFAULT_DISPLAY_CONFIG)
  const refreshDisplayConfigRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshAffiliateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastDisplayEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const lastAffiliateEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const socketProxySubscribedRef = useRef(false)

  const loadDisplayConfig = useCallback(async () => {
    try {
      const res = await getReferralDisplayPromotionConfig(themeConfig.tenantId)
      if (res?.success && res.data) {
        setDisplayConfig(mergeDisplayConfig(res.data.settings))
      } else {
        setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
      }
    } catch {
      setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
    }
  }, [themeConfig.tenantId])

  const loadAffiliateOverview = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    if (!options?.silent) {
      setLoading(true)
    }
    setError(null)

    if (options?.force) {
      clearAffiliateMeCache()
    }

    try {
      const [me, history] = await Promise.all([
        getAffiliateMe(options?.force === true),
        getAffiliateCommissionHistory(100),
      ])

      setAffiliateDisabled(false)
      setAffiliateData(me)
      setCommissionHistory(history)
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string; response?: { message?: string } }
      const status = err?.status
      const msg = (err?.response?.message || err?.message || '') as string
      const isDisabled = status === 404 || (msg && msg.toLowerCase().includes('not enabled'))

      if (isDisabled) {
        setAffiliateDisabled(true)
        setAffiliateData(null)
        setCommissionHistory([])

        if (!options?.silent) {
          Swal.fire({
            icon: 'info',
            title: displayConfig.disabledTitle,
            text: displayConfig.disabledMessage,
            confirmButtonText: 'ตกลง',
          })
        }
      } else {
        setError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ')
      }
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle])

  useEffect(() => {
    void loadDisplayConfig()
    void loadAffiliateOverview()

    return () => {
      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
        refreshAffiliateRef.current = null
      }
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  useEffect(() => {
    const handleDisplayConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        contentType?: string
        type?: string
        promotionType?: string
        timestamp?: string
        updatedAt?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      } | undefined

      const isReferralDisplayUpdate =
        detail?.type === 'referral_display_config_updated' ||
        detail?.promotionType === 'referral_display' ||
        detail?.content?.type === 'referral_display_config_updated' ||
        detail?.content?.promotionType === 'referral_display' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'referral_display' ||
          detail?.content?.promotionType === 'referral_display'
        ))

      if (!isReferralDisplayUpdate) return

      const eventKey = [
        detail?.type || '',
        detail?.promotionType || '',
        detail?.contentType || '',
        detail?.content?.type || '',
        detail?.content?.promotionType || '',
        detail?.timestamp || detail?.updatedAt || detail?.content?.updatedAt || '',
      ].join('|')

      const now = Date.now()
      if (
        lastDisplayEventRef.current.key === eventKey &&
        now - lastDisplayEventRef.current.at < 1200
      ) {
        return
      }
      lastDisplayEventRef.current = { key: eventKey, at: now }

      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
      }
      refreshDisplayConfigRef.current = setTimeout(() => {
        void loadDisplayConfig()
      }, 250)
    }

    window.addEventListener('content_update', handleDisplayConfigUpdate as EventListener)
    window.addEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)

    return () => {
      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
        refreshDisplayConfigRef.current = null
      }
      window.removeEventListener('content_update', handleDisplayConfigUpdate as EventListener)
      window.removeEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)
    }
  }, [loadDisplayConfig])

  useEffect(() => {
    const handleAffiliateConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        tenantId?: string
        enabled?: boolean
        timestamp?: string
      } | undefined

      const eventKey = [
        detail?.tenantId || '',
        String(detail?.enabled),
        detail?.timestamp || '',
      ].join('|')

      const now = Date.now()
      if (
        lastAffiliateEventRef.current.key === eventKey &&
        now - lastAffiliateEventRef.current.at < 1200
      ) {
        return
      }
      lastAffiliateEventRef.current = { key: eventKey, at: now }

      if (detail?.enabled === false) {
        setAffiliateDisabled(true)
        Swal.fire({
          icon: 'info',
          title: displayConfig.disabledTitle,
          text: displayConfig.disabledMessage,
          confirmButtonText: 'ตกลง',
        })
      } else if (detail?.enabled === true) {
        setAffiliateDisabled(false)
      }

      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
      }
      refreshAffiliateRef.current = setTimeout(() => {
        void loadAffiliateOverview({ silent: true, force: true })
      }, 250)
    }

    window.addEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)
    return () => {
      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
        refreshAffiliateRef.current = null
      }
      window.removeEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle, loadAffiliateOverview])

  useEffect(() => {
    let cleanupSocket: (() => void) | undefined
    let intervalId: ReturnType<typeof setInterval> | undefined

    const subscribe = (socket: ReturnType<typeof getWebSocketConnection>) => {
      if (!socket || socketProxySubscribedRef.current) return
      socketProxySubscribedRef.current = true

      const forwardAffiliate = (data: {
        tenantId?: string
        enabled?: boolean
        updatedBy?: string
        timestamp?: string
        broadcastedAt?: string
      }) => {
        window.dispatchEvent(new CustomEvent('affiliate_config_updated', {
          detail: {
            tenantId: data?.tenantId,
            enabled: data?.enabled,
            updatedBy: data?.updatedBy,
            timestamp: data?.timestamp || data?.broadcastedAt,
          },
        }))
      }

      const forwardContent = (data: {
        type?: string
        tenantId?: string
        contentType?: string
        promotionType?: string
        timestamp?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      }) => {
        window.dispatchEvent(new CustomEvent('content_update', { detail: data }))
        if (typeof data?.type === 'string' && data.type.trim()) {
          window.dispatchEvent(new CustomEvent(data.type, { detail: data }))
        }
      }

      socket.on('affiliate_config_updated', forwardAffiliate)
      socket.on('content_update', forwardContent)

      return () => {
        socket.off('affiliate_config_updated', forwardAffiliate)
        socket.off('content_update', forwardContent)
        socketProxySubscribedRef.current = false
      }
    }

    const trySubscribe = () => {
      const socket = getWebSocketConnection()
      if (!socket) return
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
      }
      cleanupSocket = subscribe(socket)
    }

    trySubscribe()
    if (!socketProxySubscribedRef.current) {
      intervalId = setInterval(trySubscribe, 1500)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      cleanupSocket?.()
    }
  }, [])

  useEffect(() => {
    const refreshFromServer = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void loadDisplayConfig()
    }

    const intervalId = setInterval(refreshFromServer, 30000)
    window.addEventListener('focus', refreshFromServer)
    document.addEventListener('visibilitychange', refreshFromServer)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', refreshFromServer)
      document.removeEventListener('visibilitychange', refreshFromServer)
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  const handleCopyLink = () => {
    if (affiliateDisabled || !affiliateData?.affiliateLink) return
    navigator.clipboard.writeText(affiliateData.affiliateLink)
    Swal.fire({
      icon: 'success',
      title: 'สำเร็จ!',
      text: 'คัดลอกลิงค์เรียบร้อยแล้ว',
      timer: 2000,
      showConfirmButton: false,
    })
  }

  const data: AffiliateMeData = affiliateData ?? {
    commissionRate: '0.00%',
    referredMembers: 0,
    totalEarnings: 0,
    remainingEarnings: 0,
    affiliateLink: '',
    affiliateCode: '',
  }
  const referralRate = data.commissionRate || '5%'
  const shareDescription = renderRateTemplate(displayConfig.shareDescription, referralRate)
  const commissionRateSegments = useMemo(() => {
    const segments = splitCommissionRate(data.commissionRate)
    const filteredSegments = displayConfig.hideZeroCommissionSegments
      ? segments.filter((segment) => !isZeroCommissionSegment(segment))
      : segments
    return filteredSegments.length > 0 ? filteredSegments : segments
  }, [data.commissionRate, displayConfig.hideZeroCommissionSegments])
  const remainingEarnings = Number(data.remainingEarnings || 0)
  const minCommissionWithdraw = resolveMinCommissionWithdraw(displayConfig.minCommissionWithdrawalAmount)
  const canWithdraw = !affiliateDisabled && canAffiliateCommissionWithdraw(remainingEarnings, minCommissionWithdraw)
  const withdrawShortfall = affiliateWithdrawShortfall(remainingEarnings, minCommissionWithdraw)
  const hasPartialCommission = !affiliateDisabled && remainingEarnings > 0 && !canWithdraw
  const dailyCommissionHistory = useMemo(
    () => aggregateCommissionHistoryByDay(commissionHistory),
    [commissionHistory],
  )

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error && !affiliateDisabled) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-0">{error}</p>
      </div>
    )
  }

  const content = (
    <div className="row mx__1">
      <div className="col-12 px-1 mb-3">
        <div
          className="light_sd p-3 p-md-4 overflow-hidden"
          style={{
            borderRadius: 20,
            border: '1px solid rgba(90, 161, 255, 0.28)',
            background: 'linear-gradient(135deg, rgba(8,17,53,0.95), rgba(17,52,104,0.82))',
            boxShadow: '0 18px 45px rgba(8, 16, 48, 0.28)',
          }}
        >
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-start">
            <div>
              <div
                className="d-inline-flex align-items-center px-3 py-1 mb-3"
                style={{
                  borderRadius: 999,
                  background: 'rgba(86, 144, 255, 0.18)',
                  border: '1px solid rgba(126, 172, 255, 0.25)',
                }}
              >
                <span className="c_tertiary small fw-semibold">Affiliate Dashboard</span>
              </div>
              <h4 className="mb-2 c_tertiary">{displayConfig.pageTitle}</h4>
              <p className="mb-0" style={{ maxWidth: 720, color: '#dbeafe' }}>
                {displayConfig.pageSubtitle}
              </p>
            </div>
            <div
              className="px-3 py-3 text-start"
              style={{
                minWidth: 240,
                borderRadius: 18,
                background: 'rgba(15, 29, 71, 0.78)',
                border: '1px solid rgba(121, 172, 255, 0.18)',
              }}
            >
              <div className="small text-uppercase mb-2" style={{ color: '#bfd3ff' }}>ลิงก์แนะนำของคุณ</div>
              <div className="c_tertiary fw-semibold text-break">{data.affiliateCode || '-'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6 px-1 mb-2">
        <div
          className="h-100 p-3 p-md-4"
          style={{
            borderRadius: 20,
            border: '1px solid rgba(118, 178, 255, 0.2)',
            background: 'linear-gradient(155deg, rgba(29,63,149,0.9), rgba(10,26,67,0.94))',
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <div className="small text-uppercase mb-1" style={{ color: '#bfd3ff' }}>ส่วนแบ่งรายได้</div>
              <h5 className="mb-0 c_tertiary">เรทคอมมิชชั่นที่ใช้งานอยู่</h5>
            </div>
            <div
              className="px-3 py-2"
              style={{
                borderRadius: 16,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <span className="c_tertiary fw-semibold">{commissionRateSegments.length} รายการ</span>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {(commissionRateSegments.length > 0 ? commissionRateSegments : [data.commissionRate || '0.00%']).map((segment) => (
              <span
                key={segment}
                className="px-3 py-2 fw-semibold"
                style={{
                  borderRadius: 14,
                  color: '#eaf2ff',
                  background: 'rgba(255,255,255,0.09)',
                  border: '1px solid rgba(162, 199, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {segment}
              </span>
            ))}
          </div>
        </div>
      </div>

      {[
        {
          label: 'สมาชิกแนะนำ (คน)',
          value: data.referredMembers,
          tone: 'rgba(27, 114, 94, 0.88)',
        },
        {
          label: 'รายได้ทั้งหมด (บาท)',
          value: walletService.formatCurrency(data.totalEarnings),
          tone: 'rgba(113, 72, 171, 0.88)',
        },
        {
          label: 'รายได้คงเหลือ (บาท)',
          value: walletService.formatCurrency(data.remainingEarnings),
          tone: 'rgba(165, 90, 33, 0.88)',
        },
      ].map((card) => (
        <div key={card.label} className="col-6 col-lg-2 px-1 mb-2">
          <div
            className="h-100 p-3"
            style={{
              borderRadius: 18,
              border: '1px solid rgba(135, 183, 255, 0.16)',
              background: `linear-gradient(160deg, ${card.tone}, rgba(10, 21, 55, 0.94))`,
            }}
          >
            <div className="small mb-2" style={{ color: '#bfd3ff' }}>{card.label}</div>
            <div className="c_tertiary fw-bold" style={{ fontSize: '1.15rem', lineHeight: 1.35 }}>
              {card.value}
            </div>
          </div>
        </div>
      ))}

      <div className="col-12 px-1 mb-2">
        {hasPartialCommission ? (
          <Link
            to="../withdrawal"
            className="w-100 text-decoration-none d-block p-3"
            style={{
              borderRadius: 18,
              border: '1px solid rgba(251, 191, 36, 0.35)',
              background: 'linear-gradient(160deg, rgba(58, 38, 8, 0.92), rgba(8, 18, 46, 0.94))',
            }}
          >
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div>
                <h5 className="c_tertiary mb-1">
                  <i className="fas fa-hourglass-half me-2" style={{ color: '#fbbf24' }} />
                  {displayConfig.withdrawButtonText}
                </h5>
                <p className="mb-0 small" style={{ color: '#dbeafe' }}>
                  ยอดคงเหลือ {walletService.formatCurrency(remainingEarnings)} — ต้องสะสมอีก {walletService.formatCurrency(withdrawShortfall)} บาท (ขั้นต่ำ {minCommissionWithdraw} บาท)
                </p>
              </div>
              <i className="fas fa-chevron-right c_tertiary" />
            </div>
          </Link>
        ) : (
          <Link
            to="../withdrawal"
            className="link_pmr w-100 box_row justify-content-center align-items-center font_rg border-0 py-3 text-decoration-none"
            style={{
              borderRadius: 18,
              background: canWithdraw
                ? 'linear-gradient(90deg, #31d07f 0%, #48a9ff 100%)'
                : 'linear-gradient(90deg, #6b7280 0%, #94a3b8 100%)',
              boxShadow: canWithdraw
                ? '0 12px 28px rgba(46, 208, 126, 0.18)'
                : '0 10px 22px rgba(100, 116, 139, 0.2)',
              opacity: canWithdraw ? 1 : 0.55,
              pointerEvents: canWithdraw ? 'auto' : 'none',
              cursor: canWithdraw ? 'pointer' : 'not-allowed',
            }}
            aria-disabled={!canWithdraw}
          >
            <h5 className="c_tertiary mb-0">
              <i className="fas fa-dollar-sign me-2" />
              {displayConfig.withdrawButtonText}
            </h5>
          </Link>
        )}
      </div>

      <div className="col-12 px-1">
        <div
          className="p-3 p-md-4 overflow-hidden"
          style={{
            borderRadius: 20,
            border: '1px solid rgba(91, 154, 255, 0.22)',
            background: 'rgba(8, 18, 46, 0.88)',
          }}
        >
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3 mb-3">
            <div className="c_tertiary">
              <h5 className="mb-1">{displayConfig.shareSectionTitle}</h5>
              <div className="small" style={{ color: '#dbeafe' }}>{displayConfig.linkLabel}</div>
            </div>
            <div
              className="px-3 py-2"
              style={{
                borderRadius: 14,
                background: 'rgba(49, 112, 233, 0.14)',
                border: '1px solid rgba(121, 171, 255, 0.18)',
              }}
            >
              <span className="small c_tertiary">พร้อมแชร์ได้ทันที</span>
            </div>
          </div>
          <div className="box_row align-items-stretch flex-column flex-md-row gap-2">
            <input
              type="text"
              readOnly
              value={data.affiliateLink}
              className="form-control bg_secondary c_secondary"
              style={{
                width: '100%',
                minHeight: 50,
                borderRadius: 16,
                border: '1px solid rgba(135, 183, 255, 0.16)',
              }}
              aria-label="ลิงค์แนะนำ"
            />
            <button
              type="button"
              className="button_link d-flex align-items-center justify-content-center border-0"
              style={{
                width: '100%',
                maxWidth: 220,
                minHeight: 50,
                borderRadius: 16,
                background: 'linear-gradient(90deg, #2f7cf5 0%, #4ad2ff 100%)',
              }}
              onClick={handleCopyLink}
            >
              <h5 className="mb-0 c_tertiary">
                <i className="fa fa-copy d-none d-md-inline-flex me-1" aria-hidden />
                {displayConfig.copyButtonText}
              </h5>
            </button>
          </div>
        </div>
      </div>

      <div className="col-12 mt-3 px-1">
        <div
          className="light_sd overflow-hidden"
          style={{
            borderRadius: 20,
            border: '1px solid rgba(91, 154, 255, 0.2)',
            background: 'rgba(8, 18, 46, 0.9)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center px-3 px-md-4 py-3 border-bottom border-primary-subtle">
            <div>
              <h5 className="mb-1 c_tertiary">ประวัติคอมมิชชั่นล่าสุด</h5>
            </div>
          </div>
          <table className="table bg-transparent table-striped table-huay-result text-center mb-0">
            <thead className="bg_gd_primary">
              <tr>
                <th scope="col" className="c_tertiary bg-transparent">
                  วันที่
                </th>
                <th scope="col" className="c_tertiary bg-transparent">
                  <div className="bg_secondary bdc_primary border_5 c_primary font_md py-1">
                    ยอดเสียสมาชิก
                  </div>
                </th>
                <th scope="col" className="c_tertiary bg-transparent">
                  คอมมิชชั่น
                </th>
              </tr>
            </thead>
            <tbody>
              {dailyCommissionHistory.length === 0 ? (
                <tr>
                  <td colSpan={3} className="c_primary font_md py-4">
                    {displayConfig.historyEmptyText}
                  </td>
                </tr>
              ) : (
                dailyCommissionHistory.map((item, index) => (
                  <tr key={index}>
                    <td>{formatHistoryDate(item)}</td>
                    <td>{walletService.formatCurrency(item.betAmount ?? 0)}</td>
                    <td>{walletService.formatCurrency(item.commission ?? 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="col-12 mt-3 px-1">
        <div
          className="c_tertiary mt-3 p-3 p-md-4"
          style={{
            borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(11,27,67,0.96), rgba(20,63,114,0.92))',
            border: '1px solid rgba(91, 154, 255, 0.2)',
          }}
        >
          <p className="mb-0">{shareDescription}</p>
        </div>
      </div>
    </div>
  )

  if (affiliateDisabled) {
    return (
      <div className="position-relative">
        {content}
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.4)', pointerEvents: 'auto' }}
        >
          <span className="text-danger fw-bold fs-4" style={{ textShadow: '0 0 8px rgba(255,255,255,0.8)' }}>
            {displayConfig.disabledTitle}
          </span>
        </div>
      </div>
    )
  }

  return content
}

export default AffiliateOverviewPage
